"use client";

import { createContext, useContext, useState, useEffect } from "react";
import { login as apiLogin, fetchCurrentUser, getLogoutUrl, logout as apiLogout } from "@/services/userService";
import { CNSUser } from "@/models/cns";
import { NotificationSocketProvider } from "@/components/notifications/NotificationSocketProvider";
import { ConnectorOAuthCoordinatorProvider } from "@/lib/oauth/ConnectorOAuthCoordinatorProvider";
import { useConversationSuggestionsStore } from "@/stores/useConversationSuggestionsStore";
interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: CNSUser | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearAllStates: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<CNSUser | null>(null);
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      try {
        const userData = await fetchCurrentUser();
        useConversationSuggestionsStore.getState().clearAll();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error: unknown) {
        useConversationSuggestionsStore.getState().clearAll();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const result = await apiLogin(username, password);
      const userData_0 = await fetchCurrentUser();
      // Invalidate actor-scoped projections before publishing the new identity.
      useConversationSuggestionsStore.getState().clearAll();
      setUser(userData_0);
      setIsAuthenticated(true);
      return result;
    } catch {
      useConversationSuggestionsStore.getState().clearAll();
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };
  const logout = () => {
    useConversationSuggestionsStore.getState().clearAll();
    // Form POST so the browser does a full-page navigation: backend clears session and sends
    // 302 to Auth0 v2/logout, then Auth0 sends 302 to /logged-out. With fetch(POST) the document
    // would not navigate (fetch follows redirects in-process), so the user would never hit Auth0
    // and would stay logged in there. Form submit is the standard way to trigger this redirect chain.
    const form = document.createElement("form");
    form.method = "POST";
    form.action = getLogoutUrl();
    document.body.appendChild(form);
    form.submit();
  };
  const clearAllStates = async () => {
    try {
      // Call server logout endpoint to handle session cleanup
      await apiLogout();
    } catch (error_0) {
      console.error("Failed to logout from server:", error_0);
      // Continue with local cleanup even if server call fails
    } finally {
      useConversationSuggestionsStore.getState().clearAll();
      // Clear local user state
      setUser(null);
      setIsAuthenticated(false);
      // Note: We don't redirect here as this is used for activation flow
    }
  };
  return <AuthContext.Provider value={{
    isAuthenticated,
    loading,
    user,
    login,
    logout,
    clearAllStates
  }}>
      <ConnectorOAuthCoordinatorProvider authenticated={isAuthenticated} authScopeKey={user?.id ?? null}>
        <NotificationSocketProvider>{children}</NotificationSocketProvider>
      </ConnectorOAuthCoordinatorProvider>
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}